import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { fetchBillingEnabled, startCheckout } from '@/services/subscription';

const FEATURE_KEYS = [
  'pricing.feature.calls',
  'pricing.feature.habits',
  'pricing.feature.streaks',
  'pricing.feature.zmanim',
  'pricing.feature.multilang',
] as const;

export default function PricingPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  // If billing is globally disabled, the app is free — no pricing page needed.
  useEffect(() => {
    fetchBillingEnabled().then((enabled) => {
      if (!enabled) navigate('/dashboard', { replace: true });
    });
  }, [navigate]);

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login?next=/pricing');
      return;
    }
    setRedirecting(true);
    setError('');
    try {
      await startCheckout();
    } catch (err) {
      setError((err as Error).message || 'Something went wrong');
      setRedirecting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-cream dark:bg-midnight-deep px-6 py-12">
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[400px] h-[400px] rounded-full bg-accent-100/30 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-md flex flex-col items-center text-center">
        <Link to={user ? '/dashboard' : '/'} className="mb-6 inline-flex items-center gap-2">
          <img src="/icon-192.png" alt="Zman logo" className="w-12 h-12 rounded-xl shadow" />
        </Link>

        <h1 className="font-display text-3xl font-bold text-foreground-950 mb-2 tracking-tight">
          {t('pricing.title')}
        </h1>
        <p className="text-sm text-foreground-500 mb-8 max-w-xs">{t('pricing.subtitle')}</p>

        <div className="card w-full p-6 text-start">
          <div className="flex items-baseline justify-between mb-1">
            <h2 className="text-lg font-heading font-semibold text-foreground-950">
              {t('pricing.planName')}
            </h2>
            <div>
              <span className="text-3xl font-bold text-foreground-950">{t('pricing.price')}</span>
              <span className="text-sm text-foreground-500">{t('pricing.perMonth')}</span>
            </div>
          </div>
          <p className="text-[11px] text-foreground-400 mb-5">{t('pricing.trialNote')}</p>

          <ul className="space-y-2.5 mb-6">
            {FEATURE_KEYS.map((key) => (
              <li key={key} className="flex items-center gap-2.5 text-sm text-foreground-700">
                <span className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-emerald-600 text-xs" />
                </span>
                {t(key)}
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={handleSubscribe}
            disabled={redirecting || loading}
            className="btn-primary w-full py-2.5 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {redirecting ? t('pricing.processing') : t('pricing.startTrial')}
          </button>

          {error && (
            <p className="mt-3 text-xs text-red-500 flex items-start gap-1">
              <i className="ri-error-warning-line mt-px flex-shrink-0" />
              {error}
            </p>
          )}
        </div>

        <Link to={user ? '/dashboard' : '/'} className="mt-6 text-xs text-foreground-400 hover:text-foreground-600 transition">
          {user ? '\u2190 Back to dashboard' : '\u2190 Back to home'}
        </Link>
      </div>
    </div>
  );
}
